// Landing de acceso por tenant (Rediseño mayor 2026-08-31, Módulo C del plan
// structured-marinating-snail.md) — una sola pantalla con dos vistas:
// iniciar sesión y recuperar contraseña. Vive en `<subdominio>.logiacare.com`,
// fuera de la ficha del paciente. Desde el 2026-09-13 no hay "Crear cuenta":
// las cuentas las crea solo el admin y la persona elige su contraseña con el
// enlace que le llega por correo (ActivarCuentaPage.tsx), que al guardar la
// trae acá con `?activada=1`.
//
// Login: un solo formulario usuario+contraseña — prueba primero contra
// `hce_users` (admin, lib/hce-auth.ts en la bóveda) y si no matchea intenta
// contra Medplum (Personal Médico). No usa `SignInForm` de @medplum/react
// (cero Mantine/@medplum/react en hce-chart, ver el resto del paquete) — usa
// `MedplumClient.startLogin`/`processCode` directo, mismo mecanismo de bajo
// nivel que `AuthenticationForm.tsx` del fork.
//
// Verificación en dos pasos (MT-043-A/B/C): el administrador (bóveda) y el
// personal clínico (Medplum, retenido por la bóveda) pasan por los mismos
// pasos de código y de enrolamiento (PasosMfa.tsx). Gap honesto que sigue:
// multi-membership (un email con más de un Project) no está soportado todavía.
import type { LoginAuthenticationResponse } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loginHceAdmin, obtenerEstadoSalida, recuperarAdminHce } from '@/lib/hceVault';
import { CODIGO_TICKET_VENCIDO, enrolarConfirmarAdmin, enrolarConfirmarConTicket, enrolarIniciarAdmin, enrolarIniciarConTicket, pasoDeLoginClinico, verificarConTicket } from '@/lib/hceMfa';
import { RUTA_DESCARGAR_HISTORIAS, RUTAS_HCE_ADMIN } from '@/lib/rutasHceAdmin';
import { rolDelPracticanteActual } from '@/lib/useRolDelPracticante';
import { guardarSesionHceAdmin } from './hceAdminSession';
import { PasoCodigo, PasoEnrolamiento } from './PasosMfa';
import { useTenantActual } from './useTenantActual';

/**
 * A dónde navegar después de un login exitoso — bug real encontrado en
 * verificación en vivo 2026-09-01: `main.tsx` arma `/login?next=<ruta
 * original>` en `onUnauthenticated`, pero NADA en esta pantalla leía `next`
 * ni navegaba a ningún lado después de un login correcto — el usuario
 * quedaba mirando el formulario de login para siempre, sin error, porque el
 * login SÍ había funcionado. Se valida que `next` sea una ruta relativa
 * propia (empieza con `/`, no `//`) antes de usarla — es un query param,
 * viene de afuera.
 */
function destinoTrasLogin(next: string | null, porDefecto: string): string {
  if (next && next.startsWith('/') && !next.startsWith('//')) return next;
  return porDefecto;
}

/**
 * Misma idea que `destinoTrasLogin`, pero para el rol "admin": a diferencia
 * de Personal Médico (que recién abrió su sesión de Medplum antes de
 * navegar, así que CUALQUIER ruta interna es segura), el admin no tiene
 * sesión de Medplum — un `next` que apunte a una ruta que la exige (p.ej.
 * `/dashboard`, arrastrado de una visita vieja) deja al admin en un
 * ping-pong infinito de redirect con `onUnauthenticated` (main.tsx). Por eso
 * `next` solo se honra si es una de las rutas que el admin puede renderizar.
 */
export function destinoTrasLoginAdmin(next: string | null): string {
  if (next && (RUTAS_HCE_ADMIN as readonly string[]).includes(next)) return next;
  return '/usuarios';
}

/**
 * Destino del administrador tras iniciar sesión, con la salida del consultorio en cuenta: en período de descarga la ÚNICA pantalla que le sirve es
 * «Descargar mis historias» (todo lo demás lo rechaza el servidor). Si el estado no se puede consultar, cae al destino de siempre.
 */
export async function destinoAdminTrasLogin(token: string, next: string | null, leerEstado?: typeof obtenerEstadoSalida): Promise<string> {
  try {
    if ((await (leerEstado ?? obtenerEstadoSalida)(token)).enDescarga) return RUTA_DESCARGAR_HISTORIAS;
  } catch {
    // sin estado: destino normal
  }
  return destinoTrasLoginAdmin(next);
}

type Vista = 'login' | 'recuperar';

export function LandingAcceso(): JSX.Element {
  const { t } = useTranslation();
  const tenantActual = useTenantActual();
  const [searchParams] = useSearchParams();
  const [vista, setVista] = useState<Vista>('login');
  const cuentaRecienActivada = searchParams.get('activada') === '1';

  return (
    <div className="min-h-screen bg-hce-paper flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm bg-hce-surface border border-border rounded-lg shadow-sm p-6">
        <div className="text-center mb-6">
          {tenantActual.estado === 'ok' && (
            <div className="text-sm font-medium text-hce-ink">{tenantActual.tenant.clinicName}</div>
          )}
          {tenantActual.estado === 'no-encontrado' && (
            <Alert variant="red" className="text-left">
              <AlertDescription>{t('auth.clinicaNoEncontrada')}</AlertDescription>
            </Alert>
          )}
        </div>

        {tenantActual.estado === 'ok' && (
          <>
            <div className="flex text-xs font-mono uppercase tracking-wide border-b border-border mb-5">
              {(['login', 'recuperar'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVista(v)}
                  className={`flex-1 py-2 border-b-2 -mb-px transition-colors ${
                    vista === v ? 'border-hce-pine text-hce-pine' : 'border-transparent text-hce-muted'
                  }`}
                >
                  {v === 'login' ? t('auth.tabs.iniciarSesion') : t('auth.tabs.recuperar')}
                </button>
              ))}
            </div>

            {vista === 'login' && cuentaRecienActivada && (
              <Alert className="mb-3">
                <AlertDescription>{t('auth.login.cuentaActivada')}</AlertDescription>
              </Alert>
            )}
            {vista === 'login' && <FormularioLogin subdominio={tenantActual.tenant.subdominio} />}
            {vista === 'recuperar' && <FormularioRecuperar subdominio={tenantActual.tenant.subdominio} />}
          </>
        )}
      </div>
    </div>
  );
}

/** En qué paso del acceso está la persona (MT-043-C): credenciales -> (código | enrolamiento) -> sesión. */
type Fase =
  | { tipo: 'credenciales' }
  | { tipo: 'codigo-clinico'; ticket: string }
  | { tipo: 'enrolar-clinico'; ticket: string }
  | { tipo: 'codigo-admin' }
  | { tipo: 'enrolar-admin'; enrollToken: string };

function FormularioLogin({ subdominio }: { subdominio: string }): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [fase, setFase] = useState<Fase>({ tipo: 'credenciales' });

  function volverACredenciales(mensaje?: string, comoAviso = false): void {
    setFase({ tipo: 'credenciales' });
    setPassword('');
    setError(comoAviso ? null : (mensaje ?? null));
    setAviso(comoAviso ? (mensaje ?? null) : null);
  }

  /** Salida del consultorio: si el consultorio está en período de descarga, el administrador va directo a «Descargar mis historias». */
  async function irTrasLoginAdmin(token: string): Promise<void> {
    void navigate(await destinoAdminTrasLogin(token, searchParams.get('next')));
  }

  /** Con el `code` de Medplum en la mano (login normal, o liberado por la verificación): abre la sesión y navega. */
  async function abrirSesionClinica(code: string): Promise<void> {
    await medplum.processCode(code);
    const rol = await rolDelPracticanteActual();
    void navigate(destinoTrasLogin(searchParams.get('next'), rol === 'director-tecnico-farmacia' ? '/farmacia' : '/dashboard'));
  }

  async function manejarRespuestaMedplum(resp: LoginAuthenticationResponse): Promise<void> {
    // La bóveda retiene el `code` cuando la cuenta necesita el segundo factor: aquí llega un ticket, nunca el `code` (MT-043-B).
    const paso = pasoDeLoginClinico(resp as Parameters<typeof pasoDeLoginClinico>[0]);
    if (paso.paso === 'codigo') {
      setFase({ tipo: 'codigo-clinico', ticket: paso.ticket });
      return;
    }
    if (paso.paso === 'enrolar') {
      setFase({ tipo: 'enrolar-clinico', ticket: paso.ticket });
      return;
    }
    if (paso.paso === 'multiples-clinicas') {
      setError(t('auth.login.multiplesClinicas'));
      return;
    }
    if (paso.paso === 'sesion') {
      try {
        await abrirSesionClinica(paso.code);
      } catch {
        setError(t('auth.mfa.errorSesion'));
      }
      return;
    }
    setError(t('auth.login.errorGenerico'));
  }

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setAviso(null);
    setEnviando(true);
    try {
      // Admin (bóveda) y Personal Médico (Medplum) se prueban EN PARALELO,
      // no uno tras otro — antes se esperaba `loginHceAdmin` completo antes
      // de siquiera arrancar el intento contra Medplum, así que CUALQUIER
      // cuenta de Personal Médico pagaba el viaje de red completo del
      // intento de admin (que sabemos de antemano que va a fallar) antes de
      // que el login real empezara — el retraso visible reportado en vivo
      // 2026-09-01. Mandar ambos a la vez no cambia qué credenciales llegan
      // a qué endpoint (ya se mandaban las dos, solo que en serie).
      const [admin, medplumResultado] = await Promise.allSettled([
        loginHceAdmin(subdominio, username, password),
        // scope explícito: sin esto, `userInfo.profile` (oauth2/userinfo)
        // vuelve vacío y todo lo que depende de practitionerId (Módulo G,
        // Módulo Proxy) queda ciego a quién está logueado — bug real
        // encontrado en verificación en vivo 2026-08-31, no una precaución
        // teórica.
        medplum.startLogin({ email: username, password, scope: 'openid profile email' }),
      ]);

      if (admin.status === 'fulfilled' && admin.value.ok) {
        guardarSesionHceAdmin(admin.value.token, subdominio);
        // Bug real encontrado en verificación en vivo 2026-09-01: nada acá
        // navegaba a ningún lado después de un login exitoso — el usuario
        // quedaba mirando el formulario de login para siempre, sin error
        // (porque el login SÍ había funcionado). `main.tsx` arma
        // `/login?next=<ruta original>` en `onUnauthenticated`, pero nadie
        // lo leía.
        void irTrasLoginAdmin(admin.value.token);
        return;
      }
      // Administrador con la verificación en dos pasos: la contraseña ya está verificada, falta el código (o el enrolamiento si la política lo exige).
      if (admin.status === 'fulfilled' && !admin.value.ok) {
        if (admin.value.mfaEnroll) {
          setFase({ tipo: 'enrolar-admin', enrollToken: admin.value.mfaEnroll.token });
          return;
        }
        if (admin.value.mfaRequired) {
          setFase({ tipo: 'codigo-admin' });
          return;
        }
      }
      if (medplumResultado.status === 'fulfilled') {
        await manejarRespuestaMedplum(medplumResultado.value);
        return;
      }
      setError(t('auth.login.errorGenerico'));
    } catch {
      setError(t('auth.login.errorGenerico'));
    } finally {
      setEnviando(false);
    }
  }

  // --- Personal clínico: código con el ticket ---
  async function verificarClinico(ticket: string, codigo: string): Promise<string | null> {
    const r = await verificarConTicket(ticket, codigo);
    if (!r.ok) {
      // Ticket vencido o agotado: ya no hay nada que reintentar con él, hay que volver a poner la contraseña.
      if (r.codigo === CODIGO_TICKET_VENCIDO) volverACredenciales(r.error);
      return r.error;
    }
    try {
      await abrirSesionClinica(r.code);
      return null;
    } catch {
      return t('auth.mfa.errorSesion');
    }
  }

  // --- Administrador: código con la contraseña ya escrita ---
  async function verificarAdmin(codigo: string): Promise<string | null> {
    const r = await loginHceAdmin(subdominio, username, password, { mfaCode: codigo });
    if (r.ok) {
      guardarSesionHceAdmin(r.token, subdominio);
      void irTrasLoginAdmin(r.token);
      return null;
    }
    return r.error || t('auth.login.errorGenerico');
  }

  const iniciarEnrolamientoClinico = useCallback(() => (fase.tipo === 'enrolar-clinico' ? enrolarIniciarConTicket(fase.ticket) : Promise.resolve({ ok: false as const, status: 0, error: '' })), [fase]);
  const iniciarEnrolamientoAdmin = useCallback(() => (fase.tipo === 'enrolar-admin' ? enrolarIniciarAdmin(fase.enrollToken) : Promise.resolve({ ok: false as const, status: 0, error: '' })), [fase]);

  if (fase.tipo === 'codigo-clinico') {
    return <PasoCodigo onEnviar={(c) => verificarClinico(fase.ticket, c)} onCancelar={() => volverACredenciales()} />;
  }
  if (fase.tipo === 'codigo-admin') {
    return <PasoCodigo onEnviar={(c) => verificarAdmin(c)} onCancelar={() => volverACredenciales()} />;
  }
  if (fase.tipo === 'enrolar-clinico') {
    const { ticket } = fase;
    return (
      <PasoEnrolamiento
        iniciar={iniciarEnrolamientoClinico}
        confirmar={async (c) => {
          const r = await enrolarConfirmarConTicket(ticket, c);
          // El ticket venció durante el enrolamiento: ya no hay nada que reintentar con él, se vuelve a la contraseña.
          if (!r.ok && r.codigo === CODIGO_TICKET_VENCIDO) volverACredenciales(r.error);
          return r;
        }}
        onCancelar={() => volverACredenciales()}
        onTerminar={(r) => {
          if (r.code) {
            void abrirSesionClinica(r.code).catch(() => volverACredenciales(t('auth.mfa.errorSesion')));
          } else {
            volverACredenciales(t('auth.mfa.enrolar.activadaVolverAEntrar'), true);
          }
        }}
      />
    );
  }
  if (fase.tipo === 'enrolar-admin') {
    const { enrollToken } = fase;
    return (
      <PasoEnrolamiento
        iniciar={iniciarEnrolamientoAdmin}
        confirmar={(c) => enrolarConfirmarAdmin(enrollToken, c)}
        onCancelar={() => volverACredenciales()}
        onTerminar={(r) => {
          guardarSesionHceAdmin(r.token, subdominio);
          void irTrasLoginAdmin(r.token);
        }}
      />
    );
  }

  return (
    <form onSubmit={(e) => void enviar(e)} className="space-y-3">
      {aviso && (
        <Alert>
          <AlertDescription>{aviso}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div>
        <label className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">{t('auth.login.usuario')}</label>
        <Input value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none" autoCorrect="off" required />
      </div>
      <div>
        <label className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">{t('auth.login.contrasena')}</label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </div>
      <Button type="submit" disabled={enviando} className="w-full">
        {enviando ? t('auth.login.entrando') : t('auth.login.entrar')}
      </Button>
    </form>
  );
}

/**
 * Autoservicio para los dos roles con contraseña propia (2026-09-13, pedido
 * de Sue): antes el tab "admin" solo mostraba un aviso de que había que
 * pedirle a OTRO admin que restableciera la cuenta — si hay uno solo y se le
 * olvida la contraseña, quedaba sin forma de recuperarla. Ahora manda el
 * mismo enlace que "Cambiar contraseña" del panel (recuperarAdminHce),
 * disparado por la propia persona. Personal Médico sigue usando el reset
 * nativo de Medplum (auth/resetpassword) — no es lo que pidió Sue y ese
 * camino ya funciona.
 */
function FormularioRecuperar({ subdominio }: { subdominio: string }): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [tab, setTab] = useState<'medico' | 'admin'>('medico');
  const [email, setEmail] = useState('');
  const [usuarioAdmin, setUsuarioAdmin] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [exito, setExito] = useState(false);

  function cambiarTab(v: 'medico' | 'admin'): void {
    setTab(v);
    setExito(false);
  }

  async function enviarMedico(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    try {
      await medplum.post('auth/resetpassword', { email });
    } catch {
      // Mismo mensaje pase lo que pase — nunca revela si el correo existe.
    } finally {
      setEnviando(false);
      setExito(true);
    }
  }

  async function enviarAdmin(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    await recuperarAdminHce(subdominio, usuarioAdmin);
    setEnviando(false);
    setExito(true);
  }

  return (
    <div className="space-y-3">
      <div className="flex text-xs font-mono uppercase tracking-wide gap-4">
        <button type="button" onClick={() => cambiarTab('medico')} className={tab === 'medico' ? 'text-hce-pine' : 'text-hce-muted'}>
          {t('auth.registro.tipoPersonalMedico').split(' —')[0]}
        </button>
        <button type="button" onClick={() => cambiarTab('admin')} className={tab === 'admin' ? 'text-hce-pine' : 'text-hce-muted'}>
          {t('auth.registro.tipoAdmin').split(' —')[0]}
        </button>
      </div>
      {exito ? (
        <Alert>
          <AlertDescription>{tab === 'admin' ? t('auth.recuperar.adminExito') : t('auth.recuperar.exito')}</AlertDescription>
        </Alert>
      ) : tab === 'admin' ? (
        <form onSubmit={(e) => void enviarAdmin(e)} className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">{t('auth.recuperar.adminUsuario')}</label>
            <Input value={usuarioAdmin} onChange={(e) => setUsuarioAdmin(e.target.value)} autoCapitalize="none" autoCorrect="off" required />
          </div>
          <Button type="submit" disabled={enviando} className="w-full">
            {enviando ? t('auth.recuperar.enviando') : t('auth.recuperar.enviar')}
          </Button>
        </form>
      ) : (
        <form onSubmit={(e) => void enviarMedico(e)} className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">{t('auth.recuperar.correo')}</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <Button type="submit" disabled={enviando} className="w-full">
            {enviando ? t('auth.recuperar.enviando') : t('auth.recuperar.enviar')}
          </Button>
        </form>
      )}
    </div>
  );
}
